/**
 * Date arithmetic and the quick shortcuts of the task date picker.
 */
import type { Icons } from "@/components/ui/icons";
import { formatDate } from "@/i18n";

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

/** "YYYY-MM-DD" in local time. */
export function fmtDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function fmtDateTime(d: Date, time: string): string {
  return `${fmtDate(d)}T${time}`;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isPast(d: Date, today: Date): boolean {
  return fmtDate(d) < fmtDate(today);
}

function daysFrom(today: Date, days: number): Date {
  const d = new Date(today);
  d.setDate(d.getDate() + days);
  return d;
}

function getNextSaturday(today: Date): Date {
  const day = today.getDay();
  return daysFrom(today, day === 6 ? 7 : 6 - day);
}

function getNextMonday(today: Date): Date {
  const day = today.getDay();
  return daysFrom(today, day === 0 ? 1 : day === 1 ? 7 : 8 - day);
}

export interface CalendarDay {
  date: Date;
  inMonth: boolean;
}

/** Monday-first weeks covering `month` of `year`. */
export function getMonthGrid(year: number, month: number): CalendarDay[][] {
  const firstOfMonth = new Date(year, month, 1);
  let startDow = firstOfMonth.getDay() - 1;
  if (startDow < 0) startDow = 6;

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((startDow + daysInMonth) / 7) * 7;

  const days: CalendarDay[] = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startDow + 1;
    const date = new Date(year, month, dayNum);
    days.push({ date, inMonth: dayNum >= 1 && dayNum <= daysInMonth });
  }

  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

export interface Shortcut {
  id: string;
  labelKey: "today" | "tomorrow" | "thisWeekend" | "nextWeek";
  icon: keyof typeof Icons;
  getDate: (today: Date) => Date;
  getHint: (today: Date) => string;
}

const shortWeekday = (d: Date) => formatDate(d, { weekday: "short" });

export const SHORTCUTS: Shortcut[] = [
  {
    id: "today",
    labelKey: "today",
    icon: "CalendarClock",
    getDate: (today) => today,
    getHint: shortWeekday,
  },
  {
    id: "tomorrow",
    labelKey: "tomorrow",
    icon: "Sun",
    getDate: (today) => daysFrom(today, 1),
    getHint: (today) => shortWeekday(daysFrom(today, 1)),
  },
  {
    id: "this-weekend",
    labelKey: "thisWeekend",
    icon: "Sofa",
    getDate: getNextSaturday,
    getHint: (today) => shortWeekday(getNextSaturday(today)),
  },
  {
    id: "next-week",
    labelKey: "nextWeek",
    icon: "ArrowRight",
    getDate: getNextMonday,
    getHint: (today) =>
      formatDate(getNextMonday(today), {
        weekday: "short",
        day: "numeric",
        month: "short",
      }),
  },
];
